import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import apiClient from '../utils/apiClient';
import moment from 'moment-timezone';

const KOLKATA_TIMEZONE = 'Asia/Kolkata';

const AcknowledgmentSection = () => {
  const [pendingDates, setPendingDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, date: null, count: 0 });

  useEffect(() => {
    fetchPendingAcknowledgments();
  }, []);

  const fetchPendingAcknowledgments = async () => {
    try {
      const response = await apiClient.get('/manager/pending-acknowledgments');
      setPendingDates(response.data.pendingDates || []);
    } catch (error) {
      console.error('Error fetching pending acknowledgments:', error);
      toast.error('Failed to load pending acknowledgments');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeDate = async (date, requestCount) => {
    setAcknowledging(date);
    try {
      await apiClient.post('/manager/acknowledge-date', { date });
      toast.success(`Successfully acknowledged ${requestCount} requests for ${date}`);
      fetchPendingAcknowledgments(); // Refresh the list
    } catch (error) {
      console.error('Error acknowledging requests:', error);
      toast.error('Failed to acknowledge requests');
    } finally {
      setAcknowledging(null);
      setConfirmDialog({ open: false, date: null, count: 0 });
    }
  };

  const openConfirmDialog = (date, count) => {
    setConfirmDialog({ open: true, date, count });
  };

  if (loading) {
    return (
      <Box sx={{ mb: 3, p: 3, background: 'rgba(255,255,255,0.6)', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} sx={{ color: '#20d5d5' }} />
          <Typography variant="body1" color="text.secondary">Loading acknowledgments...</Typography>
        </Box>
      </Box>
    );
  }

  if (pendingDates.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ 
        mb: 3, 
        p: 3, 
        background: 'linear-gradient(to right, #fff5f5, #fff9f5)',
        borderRadius: 2,
        border: '1px solid #ffccbc',
        boxShadow: '0 2px 8px rgba(255,152,0,0.08)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: 1.5, 
              background: '#ff9800',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PendingIcon sx={{ fontSize: 20, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#e65100">
                Pending Acknowledgments
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pendingDates.reduce((sum, d) => sum + d.totalRequests, 0)} requests across {pendingDates.length} date(s)
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Grid container spacing={1.5}>
          {pendingDates.map((dateInfo, index) => (
            <Grid item xs={12} sm={6} md={4} key={dateInfo.date}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.2 }}
              >
                <Box sx={{
                  p: 2,
                  borderRadius: 1.5,
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderColor: '#ff9800',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body1" fontWeight={600} color="#212121">
                      {moment(dateInfo.date).format('MMM DD, YYYY')}
                    </Typography>
                    <Chip 
                      label={dateInfo.totalRequests}
                      size="small"
                      sx={{ 
                        background: '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                        height: 24,
                        fontSize: '0.75rem'
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 1.5, py: 1, px: 1.5, background: '#fafafa', borderRadius: 1, border: '1px solid #f0f0f0' }}>
                    {dateInfo.periodBreakdown.map(period => (
                      <Box key={period.period} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {period.period}
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color="#424242">
                          {period.count}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    onClick={() => openConfirmDialog(dateInfo.date, dateInfo.totalRequests)}
                    disabled={acknowledging === dateInfo.date}
                    startIcon={acknowledging === dateInfo.date ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      background: '#4caf50',
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.8125rem',
                      py: 0.75,
                      boxShadow: 'none',
                      '&:hover': {
                        background: '#388e3c',
                        boxShadow: '0 2px 8px rgba(76,175,80,0.3)'
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                        color: '#9e9e9e'
                      }
                    }}
                  >
                    {acknowledging === dateInfo.date ? 'Processing' : 'Acknowledge'}
                  </Button>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, date: null, count: 0 })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1, fontSize: '1.125rem' }}>
          Confirm Acknowledgment
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: '#424242' }}>
            You are about to acknowledge <strong>{confirmDialog.count}</strong> request(s) for:
          </Typography>
          <Box sx={{ p: 1.5, background: '#f5f5f5', borderRadius: 1, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} color="#212121">
              {moment(confirmDialog.date).format('MMMM DD, YYYY')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setConfirmDialog({ open: false, date: null, count: 0 })}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              color: '#616161',
              '&:hover': {
                background: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAcknowledgeDate(confirmDialog.date, confirmDialog.count)}
            variant="contained"
            disabled={acknowledging}
            startIcon={acknowledging ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <CheckIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: '#4caf50',
              boxShadow: 'none',
              px: 3,
              '&:hover': {
                background: '#388e3c',
                boxShadow: '0 2px 8px rgba(76,175,80,0.3)'
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            {acknowledging ? 'Processing' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AcknowledgmentSection;